import React, { createContext, useContext, useEffect, useState } from 'react'
import apiConfig, { apiConfigDevelopment, apiConfigPrefixes, apiConfigProduction, apiConfigTest } from '_shared/config/api'
import _ from 'lodash'
import { configKeys } from 'utils/config/keys'
import HelperValidate from 'utils/helper-validate'
import axios from 'axios'
import SecureStoreService from 'services/store/secure-store'
import { storeKeys } from 'services/store/keys'
import HelperEncryption from 'utils/helper-encryption'
import { useSplash } from 'components/provider/SplashProvider'

export const ConfigProviderContext = createContext( {
	client: null,
	call: () => {
	},
} );

const ConfigUrlVersions = {
	V1: 'v1',
	V2: 'v2',
}

const API_VERSION_TIMEOUT_MS = 2500;

class Config {
	config = {}
	urlVersion = ConfigUrlVersions.V1
	
	constructor() {
		this.name = 'ConfigClient';
		
		this.reset();
	}
	
	reset() {
		this.config = {
			[ configKeys.API_DOMAIN ]: null,
			[ configKeys.API_KEY ]: null,
			...apiConfig,
			[ configKeys.API_GRAPHQL_ENDPOINT ]: HelperValidate.isValid( apiConfig[ configKeys.API_GRAPHQL_ENDPOINT ] ) ? apiConfig[ configKeys.API_GRAPHQL_ENDPOINT ] : 'connect/'
		}
	}
	
	setUrlVersion( version ) {
		this.urlVersion = version;
	}
	
	set( key, value ) {
		this.config = {
			...this.config,
			[ key ]: value
		}
	}
	
	get( key, fallback = null ) {
		return this.config[ key ] ?? fallback
	}
	
	switchToProduction() {
		this.config = {
			...this.config,
			...apiConfigProduction
		}
	}
	
	switchToTest() {
		this.config = {
			...this.config,
			...apiConfigTest
		}
	}
	
	switchToDevelopment() {
		this.config = {
			...this.config,
			...apiConfigDevelopment
		}
	}
	
	getApiCustomerIdPrefix() {
		return this.get( configKeys.PREFIX, '' );
	}
	
	url() {
		if( _.isEmpty( this.get( configKeys.CUSTOMER_ID ) ) ){
			return null;
		}
		
		// Check for t_ or d_
		if( this.getApiCustomerIdPrefix() !== apiConfigPrefixes.PRODUCTION ) {
			return this.createUrlTest() + this.getApiCustomerIdPrefix() + this.get( configKeys.CUSTOMER_ID )
		}
		
		// Production URL
		switch( this.urlVersion ) {
			case ConfigUrlVersions.V2:
				return this.createUrlV2() + this.get( configKeys.CUSTOMER_ID )
				
			default:
				return this.createUrl() + this.getApiCustomerIdPrefix() + this.get( configKeys.CUSTOMER_ID )
		}
	}
	
	createUrl() {
		return this.get( configKeys.API_DOMAIN );
	}
	
	createUrlV2() {
		//
		const customerId = this.get( configKeys.CUSTOMER_ID, '' ).replaceAll( '_', '' ).replaceAll( '-', '' );
		
		return _.replace( this.get( configKeys.API_DOMAIN_V2 ), '{{customer}}', customerId );
	}
	
	createUrlTest() {
		return _.replace( this.get( configKeys.API_DOMAIN_V2 ), '{{customer}}', 'test' );
	}
}

export const configClient = new Config();

const ConfigProvider = ({ config, children }) => {
	
	const { logout } = useSplash()
	
	const [ loading, setLoading ] = useState( true )
	const [ customerId, setCustomerId ] = useState( config.get( configKeys.CUSTOMER_ID ) )

	const apiVersionStorageKey = customerId => {
		return `${storeKeys.API_URL_VERSION}_${config.getApiCustomerIdPrefix()}${customerId}`;
	}
	
	const validateUrl = async () => {
		const newCustomerId = config.get( configKeys.CUSTOMER_ID )
		if( _.isEmpty( newCustomerId ) ) {
			return
		}
		
		// Set for the validation if we have to check again
		setCustomerId( newCustomerId )
		
		// Try to validate the url
		try {
			const result = await axios.get(
				config.createUrlV2() + 'available/'+ newCustomerId +'.txt',
				{
					timeout: API_VERSION_TIMEOUT_MS,
					headers: {
						'Cache-Control': 'no-cache',
						'Pragma': 'no-cache',
						'Expires': '0',
					},
				}
			)

			// Do we have a result with the data key?
			if( result ) {
				if( result.data ) {
					config.setUrlVersion( (result.data ?? '').includes('yes') ? ConfigUrlVersions.V2 : ConfigUrlVersions.V1 )
				}
			}
		} catch ( e ) {
			// Error? Then it is always v1
			config.setUrlVersion( ConfigUrlVersions.V1 )
		}

		await SecureStoreService.set( apiVersionStorageKey( newCustomerId ), config.urlVersion );
	}
	
	const call = async ( fn, ...args ) => {
		config[ fn ]( ...args )
		
		if( fn === 'set' ) {
			if( config.get( configKeys.CUSTOMER_ID ) !== customerId ) {
				await validateUrl()
			}
		}
	}
	
	const validateDomain = async () => {
		SecureStoreService
			.getMulti( [ storeKeys.DOMAIN, storeKeys.DOMAIN_PREFIX, storeKeys.EMPLOYEE_SESSION, storeKeys.ENCRYPTION_KEY, storeKeys.EMPLOYEE_GUID ] )
			.then( ( results ) => {
				if( results === null ) {
					logout()
					return;
				}
				
				const { domain, domain_prefix, employee_session, encryption_key } = results;
				
				if( domain_prefix !== null ) {
					switch( domain_prefix ) {
						case apiConfigPrefixes.TEST:
							call( 'switchToTest' );
							break;
						
						case apiConfigPrefixes.DEVELOPMENT:
							call( 'switchToDevelopment' );
							break;
						
						default:
							call( 'switchToProduction' );
					}
				}
				
				// Use a cached API version immediately and refresh it in the background.
				( async() => {
					config.set( configKeys.CUSTOMER_ID, domain );
					setCustomerId( domain );

					const cachedApiVersion = await SecureStoreService.get( apiVersionStorageKey( domain ) );
					const hasCachedApiVersion = Object.values( ConfigUrlVersions ).includes( cachedApiVersion );
					if( hasCachedApiVersion ) {
						config.setUrlVersion( cachedApiVersion );
					}

					if ( employee_session !== null ) {
						if( !_.startsWith( employee_session, '!!!' ) ) {
							if ( encryption_key !== null ) {
								HelperEncryption.setKey( encryption_key );
							}
						} else {
							logout()
						}
					}

					if( hasCachedApiVersion ) {
						setLoading( false );
						validateUrl().catch( () => {
						} );
						return;
					}

					await validateUrl();
					setLoading( false )
				})()
			}
		);
	}
	
	useEffect( () => {
		validateDomain();
	}, [] )
	
	return (
		<ConfigProviderContext.Provider
			value={{
				client: config,
				customerId: customerId,
				logout: logout,
				call
			}}>
			
			{ loading ? null : children }
		</ConfigProviderContext.Provider>
	)
}

export const useConfigProvider  = () => {
	return useContext( ConfigProviderContext );
}

export default ConfigProvider
